import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { findFAQ, faqData, quickQuestions } from "@/lib/faqData";
import { FLOATING_STACK_EVENT, getFloatingStack } from "@/lib/floatingStack";
import {
  Chat,
  X,
  PaperPlaneTilt,
  ArrowLeft,
  CaretDown,
  CaretUp,
} from "@phosphor-icons/react";

export default function ChatbotWidget() {
  const [isOpen, setIsOpen] = useState(false);
  // How many floating buttons sit below the chat bubble in the bottom-right corner.
  const [stack, setStack] = useState(getFloatingStack);

  useEffect(() => {
    const handler = (e) => {
      const d = e.detail || {};
      setStack(typeof d.stack === "number" ? d.stack : d.visible ? 1 : 0);
    };
    window.addEventListener(FLOATING_STACK_EVENT, handler);
    // Pick up a value set by a page that mounted before this listener existed.
    setStack(getFloatingStack());
    return () => window.removeEventListener(FLOATING_STACK_EVENT, handler);
  }, []);
  const [messages, setMessages] = useState([
    {
      type: "bot",
      text: "Hi! I'm here to help. Ask me anything about Scaffoindia or choose from the suggestions below.",
    },
  ]);
  const [input, setInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (isOpen) {
      scrollToBottom();
    }
  }, [messages, isOpen]);

  const handleSend = (text = input) => {
    const userText = text.trim();
    if (!userText) return;

    setMessages((prev) => [...prev, { type: "user", text: userText }]);
    setInput("");

    setIsTyping(true);
    setTimeout(() => {
      const match = findFAQ(userText);
      if (match) {
        setMessages((prev) => [
          ...prev,
          { type: "bot", text: match.answer, question: match.question },
        ]);
      } else {
        setMessages((prev) => [
          ...prev,
          {
            type: "bot",
            text: "I'm not sure about that one. Try asking differently or contact our support team directly.",
          },
        ]);
      }
      setIsTyping(false);
    }, 500);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSend();
    }
  };

  const bottomClass = stack >= 2 ? "bottom-[12.5rem]" : stack === 1 ? "bottom-28" : "bottom-6";

  if (!isOpen) {
    return (
      <Button
        onClick={() => setIsOpen(true)}
        className={`fixed ${bottomClass} right-6 h-14 w-14 rounded-full bg-orange-600 hover:bg-orange-700 shadow-lg shadow-orange-600/30 z-50 flex items-center justify-center p-0 animate-bounce transition-all duration-300 print:hidden`}
        aria-label="Open chat"
      >
        <Chat size={28} weight="fill" className="text-white" />
      </Button>
    );
  }

  return (
    <div className={`fixed ${bottomClass} right-6 w-80 sm:w-96 max-h-[500px] bg-white rounded-2xl shadow-2xl shadow-neutral-900/20 z-50 flex flex-col overflow-hidden border border-neutral-200 animate-in slide-in-from-bottom-10 fade-in duration-300 transition-all duration-300 print:hidden`}>
      {/* Header */}
      <div className="bg-orange-600 px-4 py-3 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-2">
          <Chat size={22} weight="fill" className="text-white" />
          <div>
            <p className="text-white font-bold text-sm">Scaffoindia Support</p>
            <Badge className="h-5 px-1.5 text-[10px] bg-orange-500 text-orange-100">
              Online
            </Badge>
          </div>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setIsOpen(false)}
          className="h-8 w-8 text-orange-100 hover:text-white hover:bg-orange-700"
        >
          <X size={20} />
        </Button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-3 space-y-3 max-h-72">
        {messages.map((msg, idx) => (
          <div
            key={idx}
            className={`flex ${msg.type === "user" ? "justify-end" : "justify-start"}`}
          >
            <div
              className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm ${
                msg.type === "user"
                  ? "bg-orange-600 text-white rounded-br-sm"
                  : "bg-neutral-100 text-neutral-800 rounded-bl-sm"
              }`}
            >
              <p className="whitespace-pre-wrap leading-relaxed">{msg.text}</p>
            </div>
          </div>
        ))}
        {isTyping && (
          <div className="flex justify-start">
            <div className="bg-neutral-100 rounded-2xl rounded-bl-sm px-4 py-2">
              <div className="flex gap-1">
                <span className="w-2 h-2 bg-neutral-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                <span className="w-2 h-2 bg-neutral-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                <span className="w-2 h-2 bg-neutral-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Quick questions */}
      <div className="px-3 pb-2 shrink-0">
        <div className="flex flex-wrap gap-1.5 mb-2">
          {quickQuestions.slice(0, 3).map((q) => (
            <button
              key={q}
              onClick={() => handleSend(q)}
              className="text-[10px] px-2 py-1 bg-neutral-100 hover:bg-orange-100 text-neutral-700 hover:text-orange-800 rounded-full transition-colors"
            >
              {q.length > 30 ? q.substring(0, 30) + "..." : q}
            </button>
          ))}
        </div>
      </div>

      {/* Input */}
      <div className="p-3 pt-2 border-t border-neutral-200 shrink-0">
        <div className="flex gap-2">
          <Input
            placeholder="Type your question..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            className="h-10 rounded-full border-neutral-300"
          />
          <Button
            onClick={() => handleSend()}
            size="icon"
            className="h-10 w-10 rounded-full bg-orange-600 hover:bg-orange-700 shrink-0"
          >
            <PaperPlaneTilt size={18} weight="fill" />
          </Button>
        </div>
        <div className="mt-2 text-center">
          <a
            href="https://wa.me/918920893912"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs text-neutral-500 hover:text-orange-600"
          >
            <ArrowLeft size={12} />
            Still need help? Chat on WhatsApp
          </a>
        </div>
      </div>
    </div>
  );
}